/**
 * ptbfFixationsApi — RTK Query slice cho /purchase/ptbf-fixations/*.
 * Tag type: 'PTBFFixation'.
 *
 * Sprint 3 — endpoints:
 *   GET    /purchase/ptbf-fixations/list?contract_no=&futures_month=&has_uploaded=&page=&page_size=
 *   POST   /purchase/ptbf-fixations/create
 *   GET    /purchase/ptbf-fixations/{ptbf_p_id}
 *   PUT    /purchase/ptbf-fixations/{ptbf_p_id}
 *   DELETE /purchase/ptbf-fixations/{ptbf_p_id}
 *   GET    /purchase/ptbf-fixations/{ptbf_p_id}/print           (placeholder)
 *   GET    /purchase/ptbf-fixations/{ptbf_p_id}/print/cancel
 *   GET    /purchase/ptbf-fixations/{ptbf_p_id}/print/sub
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  LocalPPrintResult,
  MessageResponse,
  PageResponse,
  PTBFFixationCreateIn,
  PTBFFixationListQuery,
  PTBFFixationOut,
  PTBFFixationUpdateIn,
} from '@/types/api'

export type PTBFFixationPrintKind = 'main' | 'cancel' | 'sub'

export interface PTBFLotTobeFixedOut {
  contract_no: string
  total_lot_tobe_fixed: number | null
  total_lot: number | null
  // is_upload / issued_date áp lên TOÀN contract_no — trả về từ lần fixation
  // gần nhất để pre-fill khi New chọn contract đã upload/issued trước đó.
  is_upload: number | null
  issued_date: string | null
}

export const ptbfFixationsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listPTBFFixations: b.query<
      PageResponse<PTBFFixationOut>,
      PTBFFixationListQuery | void
    >({
      query: (params) => ({
        url: '/purchase/ptbf-fixations/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const contractNo =
          (arg && (arg as PTBFFixationListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((it) => ({
                type: 'PTBFFixation' as const,
                id: it.ptbf_p_id,
              })),
              { type: 'PTBFFixation' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'PTBFFixation' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'PTBFFixation' as const, id: 'LIST' }]
      },
    }),
    getPTBFFixation: b.query<DataResponse<PTBFFixationOut>, number>({
      query: (id) => `/purchase/ptbf-fixations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PTBFFixation', id }],
    }),
    createPTBFFixation: b.mutation<
      DataResponse<PTBFFixationOut>,
      PTBFFixationCreateIn
    >({
      query: (body) => ({
        url: '/purchase/ptbf-fixations/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'PTBFFixation', id: 'LIST' }],
    }),
    updatePTBFFixation: b.mutation<
      DataResponse<PTBFFixationOut>,
      { id: number; body: PTBFFixationUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/ptbf-fixations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'PTBFFixation', id: arg.id },
        { type: 'PTBFFixation', id: 'LIST' },
      ],
    }),
    deletePTBFFixation: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/ptbf-fixations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'PTBFFixation', id },
        { type: 'PTBFFixation', id: 'LIST' },
      ],
    }),
    /** Lookup remaining lot_tobe_fixed (+ total_lot) per contract — match Access
     *  contract_no_AfterUpdate / btnEdit_Click. */
    getPTBFLotTobeFixed: b.query<
      DataResponse<PTBFLotTobeFixedOut>,
      { contract_no: string }
    >({
      query: ({ contract_no }) => ({
        url: '/purchase/ptbf-fixations/lot-tobe-fixed',
        params: { contract_no },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'PTBFFixation' as const, id: `TBF-${arg.contract_no}` },
      ],
    }),
    /** Print PDF (placeholder Sprint 6). `kind` = 'main' | 'cancel' | 'sub'. */
    printPTBFFixation: b.query<
      DataResponse<LocalPPrintResult>,
      { id: number; kind: PTBFFixationPrintKind }
    >({
      query: ({ id, kind }) => {
        if (kind === 'main') return `/purchase/ptbf-fixations/${id}/print`
        return `/purchase/ptbf-fixations/${id}/print/${kind}`
      },
    }),
  }),
})

export const {
  useListPTBFFixationsQuery,
  useGetPTBFFixationQuery,
  useCreatePTBFFixationMutation,
  useUpdatePTBFFixationMutation,
  useDeletePTBFFixationMutation,
  useLazyPrintPTBFFixationQuery,
  useLazyGetPTBFLotTobeFixedQuery,
} = ptbfFixationsApi
